import { useState } from "react";
import { Banknote } from "lucide-react";
import { toast } from "sonner";

import api from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { DataTable, FinanceCard, NativeSelect, money, todayIso } from "./financeUi";

export default function FinancePayments({ accounts, parties, payments, reload }) {
  const debitable = accounts.filter((a) => ["expense", "liability", "asset"].includes(a.type) && !["Cash", "Bank"].includes(a.name));
  const blank = { date: todayIso(), amount: "", mode: "Bank Transfer", debit_account: "", party_id: "", reference: "", narration: "" };
  const [form, setForm] = useState(blank);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const post = async () => {
    if (!form.debit_account) return toast.error("Select the account to debit");
    try {
      await api.post("/admin/company-finance/payments", { ...form, amount: Number(form.amount), party: form.party_id ? { party_id: form.party_id } : {} });
      toast.success("Payment posted");
      setForm(blank);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not post payment");
    }
  };

  return (
    <div className="grid gap-5 lg:grid-cols-[380px_1fr]">
      <FinanceCard testid="payment-form" title="Record payment (money out)">
        <Input data-testid="payment-date-input" onChange={set("date")} type="date" value={form.date} />
        <NativeSelect data-testid="payment-debit-select" onChange={set("debit_account")} value={form.debit_account}>
          <option value="">Debit account (Expense / Payable / Advance)…</option>
          {debitable.map((a) => <option key={a.account_id} value={a.account_id}>{a.code} · {a.name}</option>)}
        </NativeSelect>
        <NativeSelect data-testid="payment-party-select" onChange={set("party_id")} value={form.party_id}>
          <option value="">Party (optional)</option>
          {parties.map((p) => <option key={p.party_id} value={p.party_id}>{p.name} ({p.type})</option>)}
        </NativeSelect>
        <Input data-testid="payment-amount-input" onChange={set("amount")} placeholder="Amount" type="number" value={form.amount} />
        <NativeSelect data-testid="payment-mode-select" onChange={set("mode")} value={form.mode}>
          <option>Bank Transfer</option>
          <option>UPI</option>
          <option>Cheque</option>
          <option>Cash</option>
        </NativeSelect>
        <Input data-testid="payment-reference-input" onChange={set("reference")} placeholder="Reference / UTR" value={form.reference} />
        <Input data-testid="payment-narration-input" onChange={set("narration")} placeholder="Narration" value={form.narration} />
        <Button data-testid="payment-save-button" onClick={post}><Banknote className="w-4 h-4" />Post Payment</Button>
        <p className="text-xs text-muted-foreground">Credits Bank (or Cash when mode is Cash). No automatic Expert payouts — this records a manual company payment only.</p>
      </FinanceCard>
      <DataTable
        columns={[
          ["Payment", (p) => <span className="font-mono text-xs">{p.payment_number}</span>],
          ["Date", (p) => p.date],
          ["Debit account", (p) => p.debit_account_name],
          ["Party", (p) => p.party?.name || "—"],
          ["Mode", (p) => p.mode],
          ["Amount", (p) => money(p.amount), "right"],
        ]}
        rowKey={(p) => p.payment_id}
        rows={payments}
        testid="payment-table"
      />
    </div>
  );
}
